import {createStore} from 'vuex'
import enJson from '../json/en.json'
import ruJson from '../json/ru.json'
import config from '../config.json'
import { supabase } from '../supabase'

const LOCALES: Record<string, typeof enJson> = { en: enJson, ru: ruJson };

interface AuthUser {
    id: string;
    email: string;
    fullName: string;
    phone: string;
    sid: string;
}

async function loadProfile(authUser: any): Promise<AuthUser> {
    const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, phone, sid')
        .eq('id', authUser.id)
        .single();
    return {
        id: authUser.id,
        email: authUser.email || '',
        fullName: profile?.full_name || '',
        phone: profile?.phone || '',
        sid: profile?.sid || '',
    };
}

const cryptoWithUUID = crypto as Crypto & { randomUUID?: () => string };

export function generateSid(): string {
    return typeof cryptoWithUUID.randomUUID === 'function'
        ? cryptoWithUUID.randomUUID()
        : `sid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

const THEME_KEY = 'jumbada_theme';

function getInitialTheme(): 'dark' | 'light' {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
    return config.theme === 'light' ? 'light' : 'dark';
}

const LOCALE_KEY = 'jumbada_locale';

function getInitialLocale(): string {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('lang');
    if (fromUrl && LOCALES[fromUrl]) {
        localStorage.setItem(LOCALE_KEY, fromUrl);
        return fromUrl;
    }
    const saved = localStorage.getItem(LOCALE_KEY);
    return saved && LOCALES[saved] ? saved : 'en';
}

const initialLocale = getInitialLocale();

const store = createStore({
    state: {
        url: '',
        game: '',
        visible: false,
        locale: initialLocale,
        jsonData: LOCALES[initialLocale],
        games: [] as any[],
        theme: getInitialTheme() as 'dark' | 'light',
        authUser: null as AuthUser | null,
        authModalVisible: false,
        authModalMode: 'register' as 'register' | 'login',
    },
    getters: {
        getJsonData: state => state.jsonData,
        getGames: state => state.games,
        getGameDataByName: (state) => (name: string) => {
            return state.games.find(game => game.name === name);
        },
        getUrlInPopUp: state => state.url,
        getVisibleInPopUp: state => state.visible,
        getGameInPopUp: state => state.game,
        getTheme: state => state.theme,
        getLocale: state => state.locale,
        isAuthenticated: state => !!state.authUser,
        currentUser: state => state.authUser,
        getPlayerSid: state => state.authUser ? state.authUser.sid : null,
        getAuthModalVisible: state => state.authModalVisible,
        getAuthModalMode: state => state.authModalMode,
    },
    mutations: {
        SET_GAMES(state, games: any[]) {
            state.games = games;
        },
        setUrlInPopUp(state, obj) {
            state.game = obj.game;
            state.url = obj.url;
            state.visible = obj.visible;
        },
        SET_CURRENT_USER(state, user: AuthUser | null) {
            state.authUser = user;
        },
        SET_AUTH_MODAL(state, payload: { visible: boolean; mode?: 'register' | 'login' }) {
            state.authModalVisible = payload.visible;
            if (payload.mode) state.authModalMode = payload.mode;
        },
        SET_THEME(state, theme: 'dark' | 'light') {
            state.theme = theme;
            document.body.classList.toggle('theme-light', theme === 'light');
            localStorage.setItem(THEME_KEY, theme);
        },
        SET_LOCALE(state, locale: string) {
            if (!LOCALES[locale]) return;
            state.locale = locale;
            state.jsonData = LOCALES[locale];
            localStorage.setItem(LOCALE_KEY, locale);
        }
    },
    actions: {
        async loadGames({commit}) {
            try {
                const response = await fetch(`${process.env.BASE_URL}games.json`, { cache: 'no-store' });
                const games = await response.json();
                commit('SET_GAMES', games);
            } catch (e) {
                console.error('Failed to load games.json', e);
            }
        },
        async initAuth({commit}) {
            const { data } = await supabase.auth.getSession();
            if (data.session) {
                commit('SET_CURRENT_USER', await loadProfile(data.session.user));
            }
            supabase.auth.onAuthStateChange(async (_event: any, session: any) => {
                if (session) {
                    commit('SET_CURRENT_USER', await loadProfile(session.user));
                } else {
                    commit('SET_CURRENT_USER', null);
                }
            });
        },
        async registerUser({commit}, payload: { fullName: string; email: string; password: string; phone?: string }) {
            const fullName = payload.fullName.trim();
            const email = payload.email.trim().toLowerCase();
            const phone = (payload.phone || '').trim();

            if (!fullName || !email || !payload.password) {
                throw new Error('MISSING_FIELDS');
            }

            const { data, error } = await supabase.auth.signUp({
                email,
                password: payload.password,
                options: { data: { full_name: fullName, phone } }
            });

            if (error) {
                const msg = error.message.toLowerCase();
                if (msg.includes('already registered') || msg.includes('already exists')) {
                    throw new Error('EMAIL_TAKEN');
                }
                throw new Error('GENERIC');
            }

            if (!data.session) {
                throw new Error('EMAIL_CONFIRMATION_REQUIRED');
            }

            const user = await loadProfile(data.session.user);
            commit('SET_CURRENT_USER', user);
            return user;
        },
        async loginUser({commit}, payload: { email: string; password: string }) {
            const email = payload.email.trim().toLowerCase();
            const { data, error } = await supabase.auth.signInWithPassword({ email, password: payload.password });
            if (error || !data.user) {
                throw new Error('INVALID_CREDENTIALS');
            }
            const user = await loadProfile(data.user);
            commit('SET_CURRENT_USER', user);
            return user;
        },
        async logoutUser({commit}) {
            await supabase.auth.signOut();
            commit('SET_CURRENT_USER', null);
        }
    },
    modules: {}
})

export default store
